import { z } from 'zod';

const frameSchema = z.object({
  filename: z.string().optional(),
  function: z.string().optional(),
  lineno: z.number().optional(),
  colno: z.number().optional(),
  chunk_id: z.string().optional(),
  instruction_addr: z.string().optional(),
  addr_mode: z.string().optional(),
  in_app: z.boolean().optional(),
  platform: z.string().optional(),
});
const exceptionSchema = z.object({
  type: z.string().optional(),
  value: z.string().optional(),
  stacktrace: z.object({ frames: z.array(frameSchema) }).optional(),
  mechanism: z
    .object({
      type: z.string().optional(),
      handled: z.boolean().optional(),
      synthetic: z.boolean().optional(),
    })
    .optional(),
});
const errorEventSchema = z.object({
  token: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $lib: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $lib_version: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $session_id: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $device_id: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $is_identified: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $app_version: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $app_build: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $app_namespace: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $os: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $os_name: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $os_version: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $device_type: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $exception_level: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $release_id: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  service: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  environment: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  operation: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  stage: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  status: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  request_id: z.string().uuid().optional().catch(undefined),
  release: z.union([z.string(), z.number(), z.boolean()]).nullish(),
  $exception_list: z.array(exceptionSchema).optional(),
});
type ErrorEventInput = z.input<typeof errorEventSchema>;

export function sanitizeExceptionProperties(properties: ErrorEventInput) {
  const result = errorEventSchema.safeParse(properties);
  const data = result.success ? result.data : errorEventSchema.parse({});
  return {
    ...data,
    $exception_list: data.$exception_list?.map((exception) => ({
      ...exception,
      value: 'Application operation failed',
      stacktrace: exception.stacktrace
        ? {
            type: 'raw',
            frames: exception.stacktrace.frames.map((frame) => ({
              ...frame,
              filename: frame.filename?.split(/[?#]/)[0]?.replace(/\/Users\/[^/]+\//g, '/user/'),
            })),
          }
        : undefined,
    })),
    $geoip_disable: true,
    $process_person_profile: false,
  };
}
